// components/Posts/StatusStatistics.tsx
import { SpinnerIcon } from "@/components/icons";
import React from "react";
import { StatusStatisticsResponse, PostStatus } from "@/core/types/postTypes";
import { getStatisticsStyles } from "@/core/utils/statusConfig";

// Define the order of statistics display
const STATISTICS_ORDER: PostStatus[] = [
  PostStatus.DRAFT,
  PostStatus.CAPTION_GENERATED,
  PostStatus.APPROVAL_PENDING,
  PostStatus.READY,
  PostStatus.SCHEDULED,
  PostStatus.PUBLISHED,
  PostStatus.ARCHIVED,
];

// Add the missing function
const getProgressColorHex = (bgColor: string) => {
  const colorMap: Record<string, string> = {
    "bg-slate-100": "#64748b",
    "bg-gray-100": "#6b7280",
    "bg-red-100": "#ef4444",
    "bg-orange-100": "#f97316",
    "bg-amber-100": "#f59e0b",
    "bg-yellow-100": "#eab308",
    "bg-lime-100": "#84cc16",
    "bg-green-100": "#22c55e",
    "bg-emerald-100": "#10b981",
    "bg-teal-100": "#14b8a6",
    "bg-cyan-100": "#06b6d4",
    "bg-sky-100": "#0ea5e9",
    "bg-blue-100": "#3b82f6",
    "bg-indigo-100": "#6366f1",
    "bg-violet-100": "#8b5cf6",
    "bg-purple-100": "#a855f7",
    "bg-fuchsia-100": "#d946ef",
    "bg-pink-100": "#ec4899",
    "bg-rose-100": "#f43f5e",
  };

  return colorMap[bgColor] || "#6b7280";
};

interface StatusStatisticsProps {
  statistics: StatusStatisticsResponse | null;
  isLoading: boolean;
  error: string | null;
}

export const StatusStatistics: React.FC<StatusStatisticsProps> = ({
  statistics,
  isLoading,
  error,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-20">
        <SpinnerIcon className="w-5 h-5 text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
        <div className="flex items-center gap-2 text-red-700">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          <span className="font-medium">Error loading statistics</span>
        </div>
        <p className="text-red-600 text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (!statistics || !statistics.statusStatistics) {
    console.warn("Statistics or statusStatistics is missing:", statistics);
    return null;
  }

  return (
    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
      {STATISTICS_ORDER.map((status) => {
        const count = statistics.statusStatistics[status] || 0;
        const config = getStatisticsStyles(status);

        return (
          <div
            key={status}
            className="bg-white rounded-lg p-2 border border-gray-100 hover:shadow-xs transition-all duration-200 min-w-0"
            title={`${status}: ${count} posts`}
          >
            <div className="flex items-center justify-between mb-1">
              <div className={`text-lg font-bold ${config.color}`}>
                {count}
              </div>
              <div
                className={`w-4 h-4 rounded-sm ${config.bgColor} flex items-center justify-center text-xs`}
              >
                {config.icon}
              </div>
            </div>
            <div className="text-xs font-medium text-gray-700 truncate">
              {status.replace('_', ' ')}
            </div>
          </div>
        );
      })}
    </div>
  );
};
